import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { statusColumn } from './sharedColumns';
import { candidateCellColumn } from './CandidateCell';
import RowActionsCell from './RowActionsCell';
import { formatDate } from './tabFormatters';
import PipelineDataTable, { candidateShape } from './PipelineDataTable';
import TabEmptyState from './TabEmptyState';
import { registerTab } from './tabRegistry';

// Secondary screening flow — "Interview" tab (data-stage="interview" in the
// mockup). Candidates here have a booked slot and are waiting on (or have
// just finished) the interview itself.
//
// NOTE: `interview_scheduled_at` / `panel_and_prep` are best-guess field
// names from the mockup's copy ("When" / "Panel & prep" columns) — not a
// confirmed API contract yet.
const InterviewTab = ({
  candidates,
  isLoading,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  opportunityId,
  assignmentId,
  requirementLocationId,
  stateMachineKey,
  manualActions,
  onActionSuccess,
  onViewProfile,
}) => {
  const columns = useMemo(
    () => [
      candidateCellColumn,
      {
        key: 'interview_scheduled_at',
        header: 'When',
        className: 'text-sm',
        render: (_v, row) => formatDate(row.interview_scheduled_at),
      },
      {
        key: 'panel_and_prep',
        header: 'Panel & prep',
        className: 'text-sm',
        tooltip: true,
        render: (_v, row) => row.panel_and_prep || '-',
      },
      statusColumn,
      {
        key: 'row_actions',
        header: '',
        className: 'text-sm',
        render: (_v, row) => (
          <RowActionsCell
            candidateId={row.candidate_id}
            mappingId={row.id}
            status="interview"
            opportunityId={opportunityId}
            assignmentId={assignmentId}
            requirementLocationId={requirementLocationId}
            stateMachineKey={stateMachineKey}
            manualActions={manualActions}
            onActionSuccess={onActionSuccess}
            onViewProfile={onViewProfile}
          />
        ),
      },
    ],
    [
      opportunityId,
      assignmentId,
      requirementLocationId,
      stateMachineKey,
      manualActions,
      onActionSuccess,
      onViewProfile,
    ]
  );

  return (
    <PipelineDataTable
      columns={columns}
      candidates={candidates}
      isLoading={isLoading}
      page={page}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      emptyState={
        <TabEmptyState description="No candidates are in the interview stage." />
      }
    />
  );
};

InterviewTab.propTypes = {
  candidates: PropTypes.arrayOf(candidateShape),
  isLoading: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requirementLocationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  stateMachineKey: PropTypes.string,
  manualActions: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
  ),
  onActionSuccess: PropTypes.func,
  onViewProfile: PropTypes.func,
};

InterviewTab.defaultProps = {
  candidates: [],
  isLoading: false,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
  onPageChange: () => {},
  onPageSizeChange: () => {},
  opportunityId: undefined,
  assignmentId: undefined,
  requirementLocationId: undefined,
  stateMachineKey: 'secondary',
  manualActions: [],
  onActionSuccess: () => {},
  onViewProfile: () => {},
};

registerTab({
  stateMachineKey: 'secondary',
  state: 'interview',
  component: InterviewTab,
});

export default InterviewTab;
